import React from 'react';
import { Col, Card, CardBody, Table } from 'reactstrap';
import { H6 } from '../../../AbstractElements';
import LicenseRadial from './LicenseRadial';

/**
 * Group card: renders the pooled balance for a license group alongside a
 * per-member breakdown table, so users can see who's pulling from the
 * shared pool while still having the one combined summary.
 */
const LicenseGroupCard = ({ group, status }) => {
    const usage = status ? status.usagePct : 0;
    const balance = status ? status.balance : 0;
    const warn = usage > 80 && !(balance < 0);
    const overdrawn = balance < 0;

    const color = overdrawn || warn ? '#f73164' : '#00b1c2';

    const wrapperClass = [
        'license-asset-card',
        warn ? 'license-asset-card--warn' : '',
        overdrawn ? 'license-asset-card--overdrawn' : ''
    ].filter(Boolean).join(' ');

    return (
        <Col xl='12' className={wrapperClass}>
            <Card>
                <CardBody>
                    <div className='d-flex justify-content-between align-items-start'>
                        <div>
                            <H6 attrH6={{ className: 'mb-1' }}>
                                {group.group_name}
                                <span className='license-badge license-badge--consumed ms-2'>GROUP</span>
                            </H6>
                            <div className='f-12 f-light'>
                                {group.group_code} · {(group.members || (status && status.memberBreakdown) || []).length} member properties share this pool
                                {overdrawn && <span className='license-badge license-badge--overdraft ms-2'>OVERDRAWN</span>}
                            </div>
                        </div>
                    </div>

                    <div className='d-flex align-items-center justify-content-between mt-3'>
                        <div className='license-inline-stats'>
                            <div className='license-stat'>
                                <div className='v'>{status ? status.balance : '—'}</div>
                                <div className='l'>Balance</div>
                            </div>
                            <div className='license-stat'>
                                <div className='v'>{status ? status.available : '—'}</div>
                                <div className='l'>Available</div>
                            </div>
                            <div className='license-stat'>
                                <div className='v'>{status ? status.consumed : '—'}</div>
                                <div className='l'>Consumed</div>
                            </div>
                            {status && status.overdrafts > 0 && (
                                <div className='license-stat'>
                                    <div className='v'>{status.overdrafts}</div>
                                    <div className='l'>Overdraft</div>
                                </div>
                            )}
                        </div>
                        <div style={{ width: 120 }}>
                            <LicenseRadial percent={usage} color={color} height={140} />
                        </div>
                    </div>

                    {status && status.memberBreakdown && status.memberBreakdown.length > 0 && (
                        <div className='mt-3'>
                            <div className='f-12 f-light mb-2 text-uppercase' style={{ letterSpacing: 0.5 }}>
                                Member consumption
                            </div>
                            <Table size='sm' className='mb-0'>
                                <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Mall Code</th>
                                        <th className='text-end'>Consumed</th>
                                        <th className='text-end'>Overdrafts</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {status.memberBreakdown.map(m => (
                                        <tr key={m._id}>
                                            <td>{m.property_name}</td>
                                            <td>{m.property_mall_code}</td>
                                            <td className='text-end'>{m.consumed}</td>
                                            <td className='text-end'>
                                                {m.overdrafts > 0
                                                    ? <span className='license-badge license-badge--overdraft'>{m.overdrafts}</span>
                                                    : 0}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
};

export default LicenseGroupCard;
